import { $applyNodeReplacement, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from "lexical";
import { JSX, useState } from "react";
import { NodeDeleteButton } from "@/components/Editor/NodeDeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BaseAppNode, registerBaseAppNodeType } from "./BaseAppNode";
import { WindowDecorator } from "./WindowNode";

// Register this node type with BaseAppNode
registerBaseAppNodeType("price-trend");

export type SerializedPriceTrendNode = Spread<
  {
    type: "price-trend";
    version: 1;
    key: string;
    symbol: string;
    trend: "BULLISH" | "BEARISH" | "NEUTRAL";
    support: number;
    resistance: number;
    target: number;
    stopLoss?: number;
    notes?: string;
    chartUrl?: string;
  },
  SerializedLexicalNode
>;

/**
 * PriceTrendNode - 股价走势分析节点
 *
 * Per architecture: Node holds value fields directly. Content Schema is SSOT via Adapter.
 */
export class PriceTrendNode extends BaseAppNode {
  __symbol: string;
  __trend: "BULLISH" | "BEARISH" | "NEUTRAL";
  __support: number;
  __resistance: number;
  __target: number;
  __stopLoss?: number;
  __notes?: string;
  __chartUrl?: string;

  static getType(): string {
    return "price-trend";
  }

  static clone(node: PriceTrendNode): PriceTrendNode {
    return new PriceTrendNode(
      node.__symbol,
      node.__trend,
      node.__support,
      node.__resistance,
      node.__target,
      node.__stopLoss,
      node.__notes,
      node.__chartUrl,
      node.__key,
    );
  }

  constructor(
    symbol: string,
    trend: "BULLISH" | "BEARISH" | "NEUTRAL",
    support: number,
    resistance: number,
    target: number,
    stopLoss?: number,
    notes?: string,
    chartUrl?: string,
    key?: NodeKey,
  ) {
    super(false, key); // false = block-level node
    this.__symbol = symbol;
    this.__trend = trend;
    this.__support = support;
    this.__resistance = resistance;
    this.__target = target;
    this.__stopLoss = stopLoss;
    this.__notes = notes;
    this.__chartUrl = chartUrl;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = "price-trend-node-container";
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <PriceTrendDecorator node={this} />;
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedPriceTrendNode): PriceTrendNode {
    const node = $createPriceTrendNode(
      serializedNode.symbol,
      serializedNode.trend,
      serializedNode.support,
      serializedNode.resistance,
      serializedNode.target,
      serializedNode.stopLoss,
      serializedNode.notes,
      serializedNode.chartUrl,
    );
    return node;
  }

  exportJSON(): SerializedPriceTrendNode {
    return {
      type: "price-trend",
      version: 1,
      key: this.__key,
      symbol: this.__symbol,
      trend: this.__trend,
      support: this.__support,
      resistance: this.__resistance,
      target: this.__target,
      stopLoss: this.__stopLoss,
      notes: this.__notes,
      chartUrl: this.__chartUrl,
    };
  }

  // ========== Getter/Setter ==========

  getSymbol(): string {
    return this.__symbol;
  }

  setSymbol(symbol: string): void {
    const writable = this.getWritable();
    writable.__symbol = symbol;
  }

  getTrend(): "BULLISH" | "BEARISH" | "NEUTRAL" {
    return this.__trend;
  }

  setTrend(trend: "BULLISH" | "BEARISH" | "NEUTRAL"): void {
    const writable = this.getWritable();
    writable.__trend = trend;
  }

  getSupport(): number {
    return this.__support;
  }

  setSupport(support: number): void {
    const writable = this.getWritable();
    writable.__support = support;
  }

  getResistance(): number {
    return this.__resistance;
  }

  setResistance(resistance: number): void {
    const writable = this.getWritable();
    writable.__resistance = resistance;
  }

  getTarget(): number {
    return this.__target;
  }

  setTarget(target: number): void {
    const writable = this.getWritable();
    writable.__target = target;
  }

  getStopLoss(): number | null {
    return this.__stopLoss ?? null;
  }

  setStopLoss(stopLoss: number | null): void {
    const writable = this.getWritable();
    writable.__stopLoss = stopLoss ?? undefined;
  }

  getNotes(): string {
    return this.__notes || "";
  }

  setNotes(notes: string): void {
    const writable = this.getWritable();
    writable.__notes = notes;
  }

  getChartUrl(): string {
    return this.__chartUrl || "";
  }

  setChartUrl(chartUrl: string): void {
    const writable = this.getWritable();
    writable.__chartUrl = chartUrl;
  }

  // ========== 其他 ==========

  getTextContent(): string {
    const trendLabel = this.__trend === "BULLISH" ? "看涨" : this.__trend === "BEARISH" ? "看跌" : "中性";
    return `[股价走势] ${this.__symbol} - ${trendLabel} - 目标: $${this.__target}`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof PriceTrendNode)) {
      return false;
    }
    return this.__symbol === other.__symbol && this.__trend === other.__trend;
  }
}

/**
 * 股价走势节点编辑器组件
 */
function PriceTrendDecorator({ node }: { node: PriceTrendNode }) {
  const [symbol, setSymbol] = useState(node.getSymbol());
  const [trend, setTrend] = useState(node.getTrend());
  const [support, setSupport] = useState(node.getSupport());
  const [resistance, setResistance] = useState(node.getResistance());
  const [target, setTarget] = useState(node.getTarget());
  const [stopLoss, setStopLoss] = useState(node.getStopLoss() || "");
  const [notes, setNotes] = useState(node.getNotes());
  const [chartUrl, setChartUrl] = useState(node.getChartUrl());

  return (
    <WindowDecorator node={node} className="my-4 p-4 rounded-lg border bg-card shadow-sm">
      {(handleDelete) => (
        <>
          {/* Header with delete button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">股价走势分析</h3>
            <NodeDeleteButton onClick={handleDelete} title="删除节点" />
          </div>

          {/* 股票代码和趋势 */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">股票代码</Label>
              <Input
                value={symbol}
                onChange={(e) => {
                  setSymbol(e.target.value.toUpperCase());
                  node.setSymbol(e.target.value.toUpperCase());
                }}
                placeholder="TSLA"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">趋势</Label>
              <div className="flex gap-2 mt-1">
                {(["BULLISH", "BEARISH", "NEUTRAL"] as const).map((t) => (
                  <Button
                    key={t}
                    variant={trend === t ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setTrend(t);
                      node.setTrend(t);
                    }}
                  >
                    {t === "BULLISH" ? "看涨" : t === "BEARISH" ? "看跌" : "中性"}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* 价格位网格 */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm font-medium">支撑位</Label>
              <Input
                type="number"
                step="0.01"
                value={support || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setSupport(val);
                  node.setSupport(val);
                }}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">阻力位</Label>
              <Input
                type="number"
                step="0.01"
                value={resistance || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setResistance(val);
                  node.setResistance(val);
                }}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">目标价</Label>
              <Input
                type="number"
                step="0.01"
                value={target || ""}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  setTarget(val);
                  node.setTarget(val);
                }}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">止损价（可选）</Label>
              <Input
                type="number"
                step="0.01"
                value={stopLoss || ""}
                onChange={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : null;
                  setStopLoss(e.target.value);
                  node.setStopLoss(val);
                }}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
          </div>

          {/* 图表 URL */}
          <div className="mb-4">
            <Label className="text-sm font-medium">图表 URL（可选）</Label>
            <Input
              value={chartUrl}
              onChange={(e) => {
                setChartUrl(e.target.value);
                node.setChartUrl(e.target.value);
              }}
              placeholder="https://..."
              className="mt-1"
            />
          </div>

          {/* 备注 */}
          <div>
            <Label className="text-sm font-medium">分析备注</Label>
            <Textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                node.setNotes(e.target.value);
              }}
              placeholder="添加技术分析备注..."
              className="mt-1"
              rows={3}
            />
          </div>

          {/* 预览图表 */}
          {chartUrl && (
            <div className="mt-4">
              <img
                src={chartUrl}
                alt={`${symbol} 价格走势图`}
                className="w-full rounded-lg border"
                onError={(e) => {
                  e.currentTarget.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif'%3E图片加载失败%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>
          )}
        </>
      )}
    </WindowDecorator>
  );
}

/**
 * 创建 PriceTrendNode
 */
export function $createPriceTrendNode(
  symbol: string,
  trend: "BULLISH" | "BEARISH" | "NEUTRAL" = "NEUTRAL",
  support: number = 0,
  resistance: number = 0,
  target: number = 0,
  stopLoss?: number,
  notes?: string,
  chartUrl?: string,
): PriceTrendNode {
  const node = new PriceTrendNode(symbol, trend, support, resistance, target, stopLoss, notes, chartUrl);
  return $applyNodeReplacement(node);
}
